import { ProLightCirclePause } from '@nexelem/icons/ProLightCirclePause';
import { ProLightHourglassClock } from '@nexelem/icons/ProLightHourglassClock';
import { Badge, IconButton, Status } from '@nexelem/components';
import { KanbanTime } from './KanbanTime';
import { CUSTOM_ICONS } from '@nexelem/icons';
import { useCustomTranslation } from '../../../../../hooks';
import { Collapse } from '@nexelem/deprecated-theme';
import { useState } from 'react';
import { ProLightChevronUp } from '@nexelem/icons/ProLightChevronUp';
import { ProLightChevronDown } from '@nexelem/icons/ProLightChevronDown';
import { BatchKanbanViewBase } from '@nexelem/graphne-api';
import { KanbanTask } from './KanbanTask';
import { MultipleTableAvatar } from '../../../../molecules/MultipleTableAvatar';
import { parseName } from '@nexelem/utils';
import { useProductionPlanSettings } from '../../components/SettingsDrawer';

type KanbanBatchProps = {
  batch: BatchKanbanViewBase;
};

export const KanbanBatch = ({ batch }: KanbanBatchProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { tWrapper } = useCustomTranslation('products.productionPlan');
  const showOnTaskSettings = useProductionPlanSettings((state) => state.kanban.showOnTask);
  const kanbanBatchMapper = useKanbanBatchMapper();

  const tasks = batch?.tasks;

  if (!tasks || tasks.length === 0) {
    return null;
  }
  const { start, end, status, wasPaused } = batch;

  const { avatars, hasActiveDowntime } = kanbanBatchMapper(batch);

  return (
    <div className="flex flex-col justify-between rounded-xs border border-border-container bg-surface-container-lighter p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="mb-2 flex gap-2">
            {showOnTaskSettings.assignedWorkers && !!avatars?.length && <MultipleTableAvatar avatars={avatars} />}
            <Badge StartIcon={<CUSTOM_ICONS.Merge className="text-icon-darker" />}>{tasks.length}</Badge>
            {!isExpanded && (
              <>
                {wasPaused && (
                  <Status
                    color="grey"
                    Icon={ProLightCirclePause}
                    text={tWrapper('kanban.paused')}
                    variant="contained"
                  />
                )}
                {hasActiveDowntime && (
                  <Status
                    color="grey"
                    Icon={ProLightHourglassClock}
                    text={tWrapper('kanban.downtime')}
                    variant="contained"
                  />
                )}
              </>
            )}
          </div>
          <span className="mb-2 text-t20-sb">
            {tWrapper('batch')} {batch.name}
          </span>
        </div>

        <IconButton
          Icon={isExpanded ? ProLightChevronUp : ProLightChevronDown}
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
        />
      </div>

      <Collapse in={isExpanded}>
        <div className="mt-2 flex flex-col gap-2">
          {tasks.map((task) => (
            <KanbanTask className="bg-surface-primary" key={task.id} task={task} />
          ))}
        </div>
      </Collapse>

      <KanbanTime className="mt-4" end={end} start={start} status={status} />
    </div>
  );
};

const useKanbanBatchMapper = () => (batch: BatchKanbanViewBase) => {
  const { softResourceAssignments, tasks } = batch;

  return {
    avatars: softResourceAssignments?.map(({ name }) => parseName(name)),
    hasActiveDowntime: tasks?.some((task) => task.hasActiveDowntime),
  };
};
